import { Templates, Utils } from '@hellohair/types';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveTwoToneIcon from '@mui/icons-material/SaveTwoTone';
import { Box, Button, Chip, IconButton, Tab, Tabs, Typography } from '@mui/material';
import { FC, ReactNode, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataContext } from '../../../../aws/DataProvider';
import { ConfirmDialog } from '../../../modal/ConfirmDialog';
import { CustomModal } from '../../../modal/CustomModal';
import { STEP_GROUP_COLOR, stepTypeMeta } from './stepTypes';

/** Props every step editor receives from the step list. */
export interface StepEditorProps {
  survey: Templates.SurveyTemplate;
  index: number;
  /** Closes the modal, discarding anything not saved. */
  onClose: () => void;
  className?: string;
}

interface StepDialogProps {
  survey: Templates.SurveyTemplate;
  index: number;
  /** Writes the local draft state back onto the survey. */
  onSave: () => void;
  onClose: () => void;
  /** Whether the draft has unsaved edits - dismissing then asks before discarding. */
  dirty?: boolean;
  /** Content of the three tabs. A tab is omitted entirely when its slot is empty. */
  edit?: ReactNode;
  logic?: ReactNode;
  settings?: ReactNode;
}

/**
 * The modal shell every step editor shares: type header, the
 * Content / Logic / Settings tabs and the delete + cancel + save footer.
 * Individual step types only supply the fields that belong in each tab.
 */
export const StepDialog: FC<StepDialogProps> = ({ survey, index, onSave, onClose, dirty, edit, logic, settings }) => {
  const { t } = useTranslation();

  const {
    formDetailsState: [, setCurrentSurvey],
  } = useContext(DataContext);

  const step = survey.steps[index] as Templates.StepDataBase & { type: string };
  const meta = stepTypeMeta(step.type);
  const TypeIcon = meta.icon;
  const accent = STEP_GROUP_COLOR[meta.group];
  const position = (index + 1).toLocaleString('de-DE', { minimumIntegerDigits: 2 });

  const panels = [
    { key: 'edit', label: t('editor.tab.edit'), content: edit },
    { key: 'logic', label: t('editor.tab.logic'), content: logic },
    { key: 'settings', label: t('editor.tab.settings'), content: settings },
  ].filter((panel) => !!panel.content);

  const [tab, setTab] = useState(0);
  /** Which irreversible action is waiting for confirmation, if any. */
  const [confirming, setConfirming] = useState<'discard' | 'delete' | null>(null);
  const activeTab = Math.min(tab, Math.max(panels.length - 1, 0));

  /** Every dismiss path - the header ✕, Cancel, Escape and the backdrop. */
  const requestClose = () => {
    if (dirty) setConfirming('discard');
    else onClose();
  };

  const saveElement = () => {
    onSave();
    onClose();
  };

  const deleteElement = () => {
    const newState = [...survey.steps];
    newState.splice(index, 1);
    setCurrentSurvey({ ...survey, steps: newState } as Utils.DBItem<Templates.SurveyTemplate>);
    onClose();
  };

  // 'editor.discard.*' / 'editor.deleteStep.*' - same four keys per intent.
  const confirmKey = confirming === 'delete' ? 'editor.deleteStep' : 'editor.discard';

  return (
    <CustomModal variant="large" isModalOpen onModalClose={requestClose}>
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          borderRadius: '10px',
          borderTop: `4px solid ${accent}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            flexShrink: 0,
            padding: '16px 20px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" fontWeight="bold" sx={{ color: 'grey.800', flexShrink: 0 }}>
            {position}
          </Typography>
          <Chip
            size="small"
            icon={<TypeIcon sx={{ color: `${accent} !important` }} fontSize="small" />}
            label={t(meta.labelKey)}
            sx={{
              color: accent,
              borderColor: accent,
              backgroundColor: 'transparent',
              border: '1px solid',
              fontWeight: 600,
              minWidth: 0,
            }}
          />
          <Typography variant="body2" sx={{ color: 'grey.600', flexShrink: 0 }}>
            {t('editor.stepId', { id: step.id })}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <IconButton
            type="button"
            size="small"
            onClick={requestClose}
            aria-label={t('editor.action.close')}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {panels.length > 1 && (
          <Box sx={{ flexShrink: 0, borderBottom: 1, borderColor: 'divider', paddingX: '20px' }}>
            <Tabs
              value={activeTab}
              onChange={(_event, value: number) => setTab(value)}
              aria-label={t('editor.tab.ariaLabel')}
              sx={{ minHeight: '42px', '.MuiTab-root': { minHeight: '42px', textTransform: 'none' } }}
            >
              {panels.map((panel) => (
                <Tab key={panel.key} label={panel.label} id={`step-${step.id}-tab-${panel.key}`} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* Only the fields scroll - the header and the actions stay put. */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 20px 20px' }}>
          {panels[activeTab]?.content}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            flexShrink: 0,
            padding: '12px 20px',
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Button
            onClick={() => setConfirming('delete')}
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon />}
          >
            {t('editor.action.delete')}
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={requestClose} size="small" variant="outlined">
              {t('editor.action.cancel')}
            </Button>
            <Button onClick={saveElement} size="small" variant="contained" startIcon={<SaveTwoToneIcon />}>
              {t('editor.action.save')}
            </Button>
          </Box>
        </Box>

        <ConfirmDialog
          open={!!confirming}
          title={t(`${confirmKey}.title`)}
          body={t(`${confirmKey}.body`)}
          keepLabel={t(`${confirmKey}.keep`)}
          confirmLabel={t(`${confirmKey}.confirm`)}
          onCancel={() => setConfirming(null)}
          onConfirm={confirming === 'delete' ? deleteElement : onClose}
        />
      </Box>
    </CustomModal>
  );
};
